import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { ArrowLeft, Download, Printer, CheckCircle, Clock, XCircle } from 'lucide-react';

interface InvoiceDetailViewProps {
  invoice: any;
  onBack: () => void;
  type: 'sales' | 'purchase';
}

export default function InvoiceDetailView({ invoice, onBack, type }: InvoiceDetailViewProps) {
  const handleDownloadPDF = () => {
    // PDF download logic
  };

  const handlePrint = () => {
    window.print();
  };

  // Normalize data for different invoice types
  const billToName = type === 'sales' ? (invoice.client || invoice.vendor) : (invoice.vendor || invoice.supplier || invoice.client);
  const totalAmount = invoice.total || invoice.amount;
  const serviceProvider = invoice.serviceProvider || invoice.receivedFrom || 'N/A';
  const ftaReference = invoice.ftaRef || 'Pending';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-50 p-6 lg:p-8">
      {/* Header with back button */}
      <div className="mb-6 flex items-center justify-between">
        <Button variant="outline" onClick={onBack} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to {type === 'sales' ? 'Sales' : 'Purchase'} Invoices
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint} className="gap-2">
            <Printer className="w-4 h-4" />
            Print
          </Button>
          <Button className="bg-gradient-to-r from-[#0052CC] to-indigo-600 hover:from-[#0041A3] hover:to-indigo-700 gap-2" onClick={handleDownloadPDF}>
            <Download className="w-4 h-4" />
            Download PDF
          </Button>
        </div>
      </div>

      {/* Invoice Document */}
      <div className="max-w-5xl mx-auto">
        <div className="bg-white border-2 border-gray-200 rounded-xl shadow-lg p-12">
          {/* Header Section */}
          <div className="border-b-2 border-gray-300 pb-8 mb-8">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl text-gray-900 mb-4">INVOICE</h1>
                <div className="space-y-1">
                  <p className="text-lg text-gray-900">ABC Trading LLC</p>
                  <p className="text-gray-600">Office 123, Business Bay</p>
                  <p className="text-gray-600">United Arab Emirates</p>
                  <p className="text-gray-600">Phone: +971 4 123 4567</p>
                  <p className="text-gray-600">Email: info@abctrading.ae</p>
                  <Badge variant="secondary" className="text-xs mt-3">TRN: AE123456789</Badge>
                </div>
              </div>
              <div className="text-right">
                <div className="bg-gradient-to-br from-[#0052CC] to-indigo-600 text-white px-8 py-5 rounded-xl shadow-md">
                  <p className="text-xs opacity-90 mb-2 uppercase tracking-wide">Invoice Number</p>
                  <p className="text-2xl">{invoice.number}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Bill To & Invoice Details */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <p className="text-xs text-gray-600 mb-4 uppercase tracking-wide">{type === 'sales' ? 'Bill To:' : 'Vendor:'}</p>
              <p className="text-lg text-gray-900 mb-2">{billToName}</p>
              <p className="text-gray-600">United Arab Emirates</p>
              {invoice.vendorTRN && (
                <Badge variant="secondary" className="text-xs mt-2">TRN: {invoice.vendorTRN}</Badge>
              )}
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700 uppercase tracking-wide">Invoice Date:</span>
                  <span className="text-gray-900">{invoice.date}</span>
                </div>
                <Separator />
                {invoice.dueDate && (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-700 uppercase tracking-wide">Due Date:</span>
                      <span className="text-gray-900">{invoice.dueDate}</span>
                    </div>
                    <Separator />
                  </>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700 uppercase tracking-wide">Service Provider:</span>
                  <Badge className="bg-blue-600">{serviceProvider}</Badge>
                </div>
                <Separator />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700 uppercase tracking-wide">FTA Reference:</span>
                  <Badge className="bg-green-600">{ftaReference}</Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-8 overflow-hidden rounded-xl border border-gray-200">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gradient-to-r from-gray-100 to-slate-100">
                  <th className="border-b border-gray-300 p-4 text-left text-xs uppercase tracking-wide text-gray-700">Description</th>
                  <th className="border-b border-gray-300 p-4 text-right text-xs uppercase tracking-wide text-gray-700">Qty</th>
                  <th className="border-b border-gray-300 p-4 text-right text-xs uppercase tracking-wide text-gray-700">Unit Price (AED)</th>
                  <th className="border-b border-gray-300 p-4 text-right text-xs uppercase tracking-wide text-gray-700">Tax Rate</th>
                  <th className="border-b border-gray-300 p-4 text-right text-xs uppercase tracking-wide text-gray-700">Amount (AED)</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.items || []).map((item: any, index: number) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="border-b border-gray-200 p-4 text-gray-900">{item.description || 'N/A'}</td>
                    <td className="border-b border-gray-200 p-4 text-right text-gray-700">{item.quantity || 0}</td>
                    <td className="border-b border-gray-200 p-4 text-right text-gray-700">{(item.unitPrice || 0).toFixed(2)}</td>
                    <td className="border-b border-gray-200 p-4 text-right text-gray-700">{item.taxRate || 0}%</td>
                    <td className="border-b border-gray-200 p-4 text-right text-gray-900">{(item.amount || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-96 bg-gradient-to-br from-gray-50 to-slate-50 border border-gray-200 rounded-xl p-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3">
                  <span className="text-gray-700">Subtotal:</span>
                  <span className="text-lg text-gray-900">AED {(invoice.subtotal || 0).toFixed(2)}</span>
                </div>
                <Separator />
                <div className="flex justify-between items-center pb-3">
                  <span className="text-gray-700">VAT (5%):</span>
                  <span className="text-lg text-gray-900">AED {(invoice.tax || 0).toFixed(2)}</span>
                </div>
                <Separator className="bg-gray-400" />
                <div className="flex justify-between items-center pt-3">
                  <span className="text-xl text-gray-900">Total Amount:</span>
                  <span className="text-2xl text-[#0052CC]">AED {(totalAmount || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Status Section */}
          <div className="border-t-2 pt-8 mt-8">
            <div className={`p-6 rounded-xl ${
              invoice.status === 'processed' || invoice.status === 'accepted'
                ? 'bg-emerald-50 border-2 border-emerald-200' 
                : invoice.status === 'rejected'
                ? 'bg-rose-50 border-2 border-rose-200'
                : 'bg-amber-50 border-2 border-amber-200'
            }`}>
              <p className="text-xs text-gray-600 mb-2 uppercase tracking-wide">Processing Status</p>
              <div className="flex items-center gap-3">
                {invoice.status === 'processed' || invoice.status === 'accepted' ? (
                  <>
                    <CheckCircle className="w-6 h-6 text-emerald-600" />
                    <div>
                      <p className="text-lg text-emerald-900">Successfully {type === 'purchase' ? 'Accepted' : 'Processed'}</p>
                      <p className="text-sm text-emerald-700">
                        {type === 'purchase' 
                          ? `This invoice has been accepted and processed${invoice.reviewedBy ? ` by ${invoice.reviewedBy}` : ''}`
                          : `This invoice has been sent to FTA via ${serviceProvider}`
                        }
                      </p>
                    </div>
                  </>
                ) : invoice.status === 'rejected' ? (
                  <>
                    <XCircle className="w-6 h-6 text-rose-600" />
                    <div>
                      <p className="text-lg text-rose-900">Rejected</p>
                      <p className="text-sm text-rose-700">
                        {invoice.rejectionReason || 'This invoice has been rejected'}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <Clock className="w-6 h-6 text-amber-600" />
                    <div>
                      <p className="text-lg text-amber-900">Pending {type === 'purchase' ? 'Review' : 'Processing'}</p>
                      <p className="text-sm text-amber-700">
                        {type === 'purchase'
                          ? 'This invoice is awaiting your review and approval'
                          : 'This invoice is awaiting transmission to FTA'
                        }
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Footer Notes */}
          <div className="mt-8 pt-8 border-t border-gray-300">
            <div className="text-center text-gray-600 space-y-2">
              <p className="text-sm">Thank you for your business!</p>
              <p className="text-xs">This is a computer-generated invoice and does not require a signature.</p>
              <p className="text-xs">For any queries, please contact us at info@abctrading.ae or +971 4 123 4567</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
